import { useSetAbTestRequest } from 'ab-test/hooks/useSetAbTestRequest/useSetAbTestRequest'
import { useStateAbTest } from 'ab-test/hooks/useStateAbTest/useStateAbTest'
import type { FeatureFlagDecision } from 'ab-test/types'
import { getAbDecision } from 'ab-test/utils/abTestHelpers'
import { requestSDK } from 'ab-test/utils/requestSDK'
import { useCookiesContext } from 'cookies/hooks/useCookiesContext'
import { getCookiesConsent } from 'cookies/utils/getCookiesConsent'
import { useEffect, useState } from 'react'
import type { AbTestKeys } from 'types/constants/AbTestKeys'
import { abTestsInitialized } from '../../context/AbTestProvider'
import { handleDecision } from '../../utils/handleDecision'

interface UseAbTestProps {
	key: AbTestKeys
	attributes?: Record<string, unknown>
	defaultDecision?: Partial<FeatureFlagDecision>
	preventRequestSDK?: boolean
}

export const useAbTest = ({
	key,
	attributes,
	defaultDecision,
	preventRequestSDK = false,
}: UseAbTestProps) => {
	const { abTestReady, abRequested } = useStateAbTest()
	const { cookiesConsent } = useCookiesContext()
	const setAbRequested = useSetAbTestRequest()

	const [abTest, setAbTest] = useState<FeatureFlagDecision | undefined>()

	useEffect(() => {
		requestSDK(abRequested, preventRequestSDK, setAbRequested)
	}, [abRequested, preventRequestSDK, setAbRequested])

	useEffect(() => {
		if (abTestReady) {
			const handleAbTest = async () => {
				const abTestActivated = abTestsInitialized.includes(key)
				const isCookieConsentAnswered = cookiesConsent !== undefined

				if (!abTestActivated && isCookieConsentAnswered) {
					abTestsInitialized.push(key)
				}

				const getUserContext = (await import('ab-test/utils/userContext'))
					.getUserContext
				const userContext = getUserContext()

				const isAnalyticsConsent = getCookiesConsent()?.analyticsConsent

				if (userContext) {
					const decision = handleDecision({
						userContext,
						key,
						attributes,
						shouldSendEvent: Boolean(
							cookiesConsent && isAnalyticsConsent && !abTestActivated
						),
					})

					if (decision) {
						setAbTest(decision)
					}
				}
			}
			void handleAbTest()
		}
	}, [abTestReady, cookiesConsent, key, attributes])

	return getAbDecision(abTest, defaultDecision)
}
